import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PaymentsAPI, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  CreditCard, Loader2, ShieldCheck, Flame, ArrowRight, Check, Lock, Crown,
} from "lucide-react";

// Map slug (URL) -> lookup_key + display info
const PLAN_INFO = {
  "pro-monthly": {
    lookup_key: "pro_monthly",
    name: "Pro — Monthly", price: 20, period: "month",
    unlocks: "Unlimited Halftone Studio · All 12 halftone styles · Smart underbase · 5 AI tool uses / month",
    tier: "pro",
  },
  "creator-monthly": {
    lookup_key: "creator_monthly",
    name: "Creator — Monthly", price: 45, period: "month",
    unlocks: "Everything: Halftone + AI Vector + Watermark + Photo Upgrader + auto DTF — unlimited",
    tier: "creator", highlight: true,
  },
  "halftone-lifetime": {
    lookup_key: "halftone_lifetime",
    name: "Halftone Lifetime", price: 50, period: "one-time",
    unlocks: "Halftone Studio forever · All 12 halftone styles · Every future halftone update",
    tier: "lifetime",
  },
  "all-access-lifetime": {
    lookup_key: "all_access_lifetime",
    name: "All-Access Lifetime", price: 100, period: "one-time",
    unlocks: "Everything forever: all halftone + every AI tool + every future feature we ship",
    tier: "lifetime", highlight: true,
  },
};

export default function PurchasePage() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const params = new URLSearchParams(loc.search);
  const planKey = params.get("plan") || "creator-monthly";
  const plan = PLAN_INFO[planKey] || PLAN_INFO["creator-monthly"];
  const [busy, setBusy] = React.useState(false);

  const startCheckout = async () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`);
      return;
    }
    setBusy(true);
    try {
      const r = await PaymentsAPI.checkout(plan.lookup_key, window.location.origin);
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        throw new Error("Missing checkout url");
      }
    } catch (err) {
      toast({ title: "Couldn't start checkout", description: apiErrorMessage(err, "Try again"), variant: "destructive" });
      setBusy(false);
    }
  };

  return (
    <div className="grain">
      <Navbar />
      <section className="relative min-h-[85vh] py-16">
        <div className="forge-glow absolute inset-0" />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
          <span className="chip"><CreditCard className="h-3 w-3 text-[#4aa4ff]" /> Checkout · {plan.name}</span>
          <h1 className="display-font mt-3 text-4xl font-bold sm:text-5xl">
            You&apos;re one click away from <span className="ember-text">{plan.name}</span>.
          </h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            We use Stripe for secure card, Apple Pay, Google Pay and bank payments. You&apos;ll come back to Furnace Design Lab with your plan active the moment Stripe confirms.
          </p>

          <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_1fr]">
            {/* Plan card */}
            <div className={`rounded-2xl p-8 ${plan.highlight ? "chrome-border shadow-xl shadow-blue-950/40" : "border border-border bg-card"}`}>
              {plan.highlight && <div className="mb-3 inline-flex items-center gap-1 rounded-full bg-[#4aa4ff] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#05070d]"><Crown className="h-3 w-3" /> Most popular</div>}
              <h3 className="display-font text-2xl font-bold">{plan.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="display-font text-5xl font-bold">${plan.price}</span>
                <span className="mb-1 text-sm text-muted-foreground">/ {plan.period}</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{plan.unlocks}</p>

              <ul className="mt-6 space-y-2 text-sm">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 tick" /> <span className="text-foreground/90">Instant activation on payment</span></li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 tick" /> <span className="text-foreground/90">15-day money-back guarantee</span></li>
                {plan.period === "month" ? (
                  <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 tick" /> <span className="text-foreground/90">Cancel anytime from your Stripe receipt email</span></li>
                ) : (
                  <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 tick" /> <span className="text-foreground/90">One-time payment · yours forever · lifetime updates</span></li>
                )}
              </ul>

              <Button
                onClick={startCheckout}
                disabled={busy}
                className="btn-ember mt-8 h-12 w-full gap-2 text-base font-semibold"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                Checkout with Stripe — ${plan.price}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <p className="mt-3 text-center text-[11px] text-muted-foreground">Secure checkout · powered by Stripe · card, Apple Pay, Google Pay accepted</p>
            </div>

            {/* Trust rail */}
            <aside className="space-y-4">
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[#4aa4ff]" /><h3 className="display-font text-lg font-bold">Secured by Stripe</h3></div>
                <p className="mt-2 text-sm text-muted-foreground">Your card never touches our servers. Payments are processed by Stripe, PCI-DSS Level 1. We only store your plan status.</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3"><Flame className="h-5 w-5 text-[#f5c845]" /><h3 className="display-font text-lg font-bold">Instant unlock</h3></div>
                <p className="mt-2 text-sm text-muted-foreground">Once Stripe confirms your payment, your plan activates automatically. No emails to send, no waiting.</p>
              </div>
              <div className="rounded-2xl chrome-border p-6">
                <div className="flex items-center gap-3"><Crown className="h-5 w-5 text-[#f5c845]" /><h3 className="display-font text-lg font-bold">15-day money-back</h3></div>
                <p className="mt-2 text-sm text-muted-foreground">If we&apos;re not saving you 30+ minutes per print job, email us for a no-questions refund inside 15 days.</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Prefer a different way to pay?</div>
                <p className="mt-2 text-sm text-muted-foreground">We also accept Zelle, CashApp, Venmo and wire for teams / partners.</p>
                <Button variant="outline" className="mt-3 gap-2" onClick={() => navigate("/contact")}>Contact us <ArrowRight className="h-3.5 w-3.5" /></Button>
              </div>
            </aside>
          </div>

          <div className="mt-10 text-center">
            <button className="text-sm text-[#4aa4ff] hover:underline" onClick={() => navigate("/pricing")}>← Back to all plans</button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
